import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { emitLifecycle } from "@/lib/lifecycle";
import { clearAuthHint, setAuthHint } from "@/lib/authHint";
import { applyOwnerExclusionIfNeeded } from "@/lib/ownerExclusion";

const AuthCtx = createContext(null);

// Phase 6.AP — reduced from 4s to 2s per user feedback. Production
// /auth/me must resolve (or be force-resolved) within this window so
// the Analyze page never sits in auth_loading limbo.
const AUTH_ME_TIMEOUT_MS = 2000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 6.AV — distinguish "auth check timed out / network error"
  // from "backend definitively said you are unauthed". Only the latter
  // is a true unauthed signal; the former leaves cookie-only auth
  // ambiguous. The Analyze page uses this to decide whether to render
  // the pre-emptive "Sign in" gate or to optimistically fire a
  // backend-validated request (so cookie sessions still work).
  const [authProbedUnauthed, setAuthProbedUnauthed] = useState(false);

  // Phase 6.AV — bounded background /auth/me retry.
  // The background retry previously had no timeout and could hang
  // forever on a slow backend, leaving the UI stuck in an ambiguous
  // "user=null, hasStoredToken=null, but might still be cookie-authed"
  // state. Bound the wait so we eventually fall through to one of:
  //   * authed (success)
  //   * unauthed (real 401 -> authProbedUnauthed=true)
  //   * timed out (authProbedUnauthed STAYS false -> Analyze gate
  //     proceeds optimistically and lets the backend adjudicate)
  const AUTH_ME_BACKGROUND_TIMEOUT_MS = 6000;

  const checkAuth = useCallback(async ({ background = false } = {}) => {
    const t0 = Date.now();
    try {
      // Phase 6.AO — race /auth/me against a hard timeout. Whichever
      // settles first wins.
      // Phase 6.AQ — background retries skip the timeout race; they
      // can take as long as the network needs. Only the initial
      // foreground call has the hard cap so the UI doesn't block.
      const meCall = api.get("/auth/me");
      const res = background
        ? await Promise.race([
            meCall,
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error(`auth_me_background_timeout_${AUTH_ME_BACKGROUND_TIMEOUT_MS}ms`)),
                AUTH_ME_BACKGROUND_TIMEOUT_MS,
              ),
            ),
          ])
        : await Promise.race([
            meCall,
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error(`auth_me_timeout_${AUTH_ME_TIMEOUT_MS}ms`)),
                AUTH_ME_TIMEOUT_MS,
              ),
            ),
          ]);
      setUser(res.data.user);
      // Sub-pass 2B Item 2 — plant the persistent owner-exclusion marker
      // whenever a server-trusted admin user is confirmed. Idempotent;
      // subsequent refreshes keep it planted even after logout.
      try {
        applyOwnerExclusionIfNeeded(res.data.user, async () => {
          const mod = await import("@/lib/analytics");
          return mod?._testGetPosthog?.() || null;
        });
      } catch { /* never break auth on exclusion helper failure */ }
      // Phase 6.AV — successful auth_me proves the user IS authed
      // (either via cookie or bearer). Clear the unauthed-probe flag
      // so a previous false-positive doesn't keep the gate up.
      setAuthProbedUnauthed(false);
      try {
        emitLifecycle(background ? "auth_background_resolved" : "auth_resolved", {
          extra: {
            outcome: "authed",
            elapsed_ms: Date.now() - t0,
            user_id: res.data?.user?.user_id || null,
            background: !!background,
          },
        });
      } catch { /* swallow */ }
    } catch (err) {
      const isTimeout = String(err?.message || "").startsWith("auth_me_timeout_")
        || String(err?.message || "").startsWith("auth_me_background_timeout_");
      const status = err?.response?.status;
      // Only clear user on real auth failure; on timeout we keep
      // whatever user we already have (could be null on first run).
      if (!isTimeout) {
        setUser(null);
        // Phase 6.AV — definitive unauthed only when the backend
        // explicitly said so. Network errors and timeouts leave
        // authProbedUnauthed unchanged so the Analyze gate can still
        // proceed optimistically (cookie auth might still be valid).
        if (status === 401 || status === 403) {
          setAuthProbedUnauthed(true);
        }
      }
      if (!isTimeout && (status === 401 || status === 403)) {
        clearAuthHint();
      }
      try {
        emitLifecycle(isTimeout ? "auth_check_timed_out" : "auth_resolved", {
          extra: {
            outcome: isTimeout ? "timed_out" : (status ? `http_${status}` : "network_error"),
            elapsed_ms: Date.now() - t0,
            error_message: String(err?.message || "").slice(0, 240),
            token_kept: isTimeout,
            background: !!background,
          },
        });
      } catch { /* swallow */ }
      // Phase 6.AQ — on initial timeout, schedule ONE background
      // retry. Don't recurse forever; one retry is enough to handle a
      // cold-start /auth/me. If the retry succeeds, setUser updates
      // and any auth-gated UI re-renders cleanly.
      if (isTimeout && !background) {
        setTimeout(() => { checkAuth({ background: true }); }, 1500);
      }
    } finally {
      // Only flip loading=false on the FOREGROUND call; the background
      // retry should never put the UI back into "loading" state.
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me check if returning from OAuth callback so AuthCallback can establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Phase 6.AS — listen for the global "stockaily:auth_failed" event
  // dispatched by the axios response interceptor whenever ANY backend
  // route (other than /auth/me itself) returns 401 with a bearer
  // attached. This is the canonical signal that "the token I am
  // sending is no longer valid against THIS backend's DB" — either
  // expired, from a different env, or a legacy session_id.
  //
  // Synchronously: drop `user` so the navbar / paywall / any
  // auth-gated UI stops claiming the user is logged in. The token
  // was already removed by the interceptor.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onAuthFailed = (e) => {
      try {
        emitLifecycle("auth_global_401_observed", {
          extra: {
            from_url: e?.detail?.from_url || null,
            request_id: e?.detail?.request_id || null,
          },
        });
      } catch { /* swallow */ }
      // Force the AuthContext to reflect the unauthed state. The
      // interceptor already cleared the token; we just need to drop
      // the user.
      setUser(null);
      // Phase 6.AV — the backend just told us the request was
      // unauthed. Promote authProbedUnauthed so the Analyze gate flips
      // to the "Sign in" state instead of looping the optimistic
      // proceed.
      setAuthProbedUnauthed(true);
    };
    window.addEventListener("stockaily:auth_failed", onAuthFailed);
    return () => window.removeEventListener("stockaily:auth_failed", onAuthFailed);
  }, []);

  const loginWithCredentials = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setAuthHint();
    setUser(res.data.user);
    try {
      applyOwnerExclusionIfNeeded(res.data.user, async () => {
        const mod = await import("@/lib/analytics");
        return mod?._testGetPosthog?.() || null;
      });
    } catch { /* swallow */ }
    setAuthProbedUnauthed(false);
    return res.data.user;
  }, []);

  const signupWithCredentials = useCallback(async (email, password, name) => {
    const res = await api.post("/auth/signup", { email, password, name });
    setAuthHint();
    setUser(res.data.user);
    try {
      applyOwnerExclusionIfNeeded(res.data.user, async () => {
        const mod = await import("@/lib/analytics");
        return mod?._testGetPosthog?.() || null;
      });
    } catch { /* swallow */ }
    setAuthProbedUnauthed(false);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearAuthHint();
    setUser(null);
    setAuthProbedUnauthed(true);
  }, []);

  const logoutAllSessions = useCallback(async () => {
    try { await api.post("/auth/logout_all"); } catch {}
    clearAuthHint();
    setUser(null);
    setAuthProbedUnauthed(true);
  }, []);

  const value = useMemo(() => ({
    user, loading, setUser, checkAuth,
    loginWithCredentials, signupWithCredentials, logout, logoutAllSessions,
    authProbedUnauthed,
  }), [user, loading, checkAuth, loginWithCredentials, signupWithCredentials, logout, logoutAllSessions, authProbedUnauthed]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}
